import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    PieChart,
    TrendingUp,
    Shield,
    MapPin,
    DollarSign,
    Calendar,
    Users,
    CheckCircle,
    ArrowRight,
    Star,
    Building2,
    Target,
    Award,
    Heart,
    Sparkles,
    Eye,
    Bookmark,
    MessageCircle,
    Clock,
    BarChart3,
    Building,
    Home,
    Landmark,
    FileText,
    Zap,
    Globe,
    ChevronDown,
    ChevronUp
} from 'lucide-react';

import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import { usePageMetadata } from '../../hooks/usePageMetadata';

const FractionalPropertyInvestmentNigeria = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);

    // Set page metadata and canonical URL
    usePageMetadata('fractionalProperty');

    useEffect(() => {
        setIsVisible(true);
    }, []);

    // Key Features
    const features = [
        {
            icon: PieChart,
            title: "Low Entry Cost",
            description: "Start investing with as little as $500 and own a fraction of premium properties"
        },
        {
            icon: Building2,
            title: "Diversified Portfolio",
            description: "Spread your investment across multiple properties to reduce risk"
        },
        {
            icon: Users,
            title: "Professional Management",
            description: "Expert property management team handles all operations and maintenance"
        },
        {
            icon: Zap,
            title: "Liquidity Options",
            description: "Buy and sell fractional shares on our secondary market platform"
        }
    ];

    // Investment Stats
    const stats = [
        { number: "8-15%", label: "Annual ROI", icon: TrendingUp, color: "text-green-600" },
        { number: "1-3 years", label: "Investment Period", icon: Calendar, color: "text-blue-600" },
        { number: "$500", label: "Minimum Investment", icon: DollarSign, color: "text-purple-600" },
        { number: "100+", label: "Fractional Properties", icon: PieChart, color: "text-orange-600" }
    ];

    // Property Types
    const propertyTypes = [
        {
            title: "Luxury Apartments",
            description: "High-end apartments in prime locations with modern amenities",
            price: "$500 - $5,000",
            roi: "10-15%",
            shares: "100 shares",
            image: "🏢"
        },
        {
            title: "Commercial Buildings",
            description: "Office buildings and retail spaces in business districts",
            price: "$1,000 - $10,000",
            roi: "12-18%",
            shares: "50 shares",
            image: "🏬"
        },
        {
            title: "Residential Complexes",
            description: "Multi-unit residential developments",
            price: "$500 - $3,000",
            roi: "8-12%",
            shares: "200 shares",
            image: "🏘️"
        },
        {
            title: "Land Development",
            description: "Raw land for future development projects",
            price: "$200 - $2,000",
            roi: "15-25%",
            shares: "500 shares",
            image: "🏗️"
        }
    ];

    // Investment Benefits
    const benefits = [
        {
            title: "Lower Risk",
            description: "Diversified across multiple properties reduces individual property risk",
            icon: Shield
        },
        {
            title: "Passive Income",
            description: "Regular dividend payments from rental income and property appreciation",
            icon: DollarSign
        },
        {
            title: "Easy Access",
            description: "Invest and manage your portfolio through our user-friendly platform",
            icon: Globe
        },
        {
            title: "Transparency",
            description: "Full transparency with regular reports on property performance",
            icon: Eye
        }
    ];

    // Investment Process
    const processSteps = [
        {
            step: "01",
            title: "Browse Properties",
            description: "Explore our curated selection of fractional properties"
        },
        {
            step: "02",
            title: "Choose Shares",
            description: "Select the number of shares you want to purchase"
        },
        {
            step: "03",
            title: "Complete Investment",
            description: "Secure payment and receive your property shares"
        },
        {
            step: "04",
            title: "Track Performance",
            description: "Monitor your investment through our dashboard"
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            
            {/* Hero Section */}
            <section className="relative pt-20 pb-16 bg-white">
                <div className="container mx-auto px-6 relative z-10">
                    <div className={`text-center max-w-4xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                            <PieChart className="w-4 h-4" />
                            Fractional Property Investment Nigeria
                        </div>
                        
                        <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                            Own a Fraction of
                            <span className="text-blue-600"> Premium Properties</span>
                        </h1>
                        
                        <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                            Invest in Nigeria's best real estate properties with fractional ownership. 
                            Start with as little as $500 and own shares in premium properties across the country.
                        </p>
                        
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 hover:-translate-y-1">
                                Start Investing
                                <ArrowRight className="w-5 h-5" />
                            </button>
                            <button className="inline-flex items-center gap-2 bg-white text-gray-700 px-8 py-4 rounded-xl font-semibold border border-gray-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                                <Eye className="w-5 h-5" />
                                View Properties
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Why Choose Fractional Investment?
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Fractional property investment makes premium real estate accessible to everyone with lower entry costs and diversified risk.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-6">
                                    <feature.icon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Property Types Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Available Fractional Properties
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Choose from our diverse portfolio of fractional properties across Nigeria's major cities.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {propertyTypes.map((property, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group border border-gray-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="h-48 bg-blue-50 flex items-center justify-center text-6xl">
                                    {property.image}
                                </div>
                                
                                <div className="p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-pink-600 transition-colors duration-300">
                                        {property.title}
                                    </h3>
                                    <p className="text-gray-600 mb-4 leading-relaxed">
                                        {property.description}
                                    </p>
                                    
                                    <div className="space-y-2 mb-6">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Share Price:</span>
                                            <span className="font-semibold text-gray-900">{property.price}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Expected ROI:</span>
                                            <span className="font-semibold text-green-600">{property.roi}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Total Shares:</span>
                                            <span className="font-semibold text-pink-600">{property.shares}</span>
                                        </div>
                                    </div>
                                    
                                    <button className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                                        Buy Shares
                                        <ArrowRight className="w-4 h-4 inline ml-2" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Fractional Investment Benefits
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Fractional property investment offers unique advantages for investors seeking diversification and lower entry costs.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {benefits.map((benefit, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-6">
                                    <benefit.icon className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Investment Process Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Simple Investment Process
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Our streamlined process makes fractional property investment simple and accessible.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {processSteps.map((step, index) => (
                            <div
                                key={index}
                                className={`text-center transition-all duration-1000 delay-${index * 100} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                            >
                                <div className="relative mb-8">
                                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-pink-600 to-blue-600 rounded-full text-white text-2xl font-bold mb-4">
                                        {step.step}
                                    </div>
                                    {index < processSteps.length - 1 && (
                                        <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-blue-600 -translate-x-1/2"></div>
                                    )}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-blue-600">
                <div className="container mx-auto px-6 text-center">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                            Ready to Start Fractional Investing?
                        </h2>
                        <p className="text-xl text-pink-100 mb-8">
                            Join thousands of investors who have made premium real estate accessible through fractional ownership.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="inline-flex items-center gap-2 bg-white text-pink-600 px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                                Start Your Investment
                                <ArrowRight className="w-5 h-5" />
                            </button>
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 bg-transparent text-white px-8 py-4 rounded-xl font-semibold border-2 border-white hover:bg-white hover:text-pink-600 transition-all duration-300 hover:-translate-y-1"
                            >
                                <MessageCircle className="w-5 h-5" />
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Learn More About Fractional Property Investment in Nigeria Section */}
            <section className="bg-white py-12 px-4 border-t border-gray-200">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-8">
                        <button
                            onClick={() => setIsLearnMoreOpen(!isLearnMoreOpen)}
                            className="flex items-center justify-center gap-2 mx-auto bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors duration-300 border border-gray-300"
                            aria-expanded={isLearnMoreOpen}
                            aria-controls="fractional-content"
                        >
                            <span className="font-medium">
                                {isLearnMoreOpen ? 'Hide Learn More About Fractional Property Investment in Nigeria' : 'Learn More About Fractional Property Investment in Nigeria'}
                            </span>
                            {isLearnMoreOpen ? (
                                <ChevronUp className="w-4 h-4 text-gray-600" />
                            ) : (
                                <ChevronDown className="w-4 h-4 text-gray-600" />
                            )}
                        </button>
                    </div>

                    <div
                        id="fractional-content"
                        className={`overflow-hidden transition-all duration-500 ease-in-out ${isLearnMoreOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
                            }`}
                    >
                        <article className="prose prose-lg max-w-none">
                            {/* Introduction */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Introduction</h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    In Nigeria, real estate has for many years been regarded as one of the safest routes to wealth creation. However, the high cost of capital required to own property has often been an impediment for many Nigerians, especially those in the diaspora. Fractional property investment in the country changes that narrative.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    With this model, investors can team up and collectively own a piece of property—whether a luxury apartment, gated estate, or commercial hub. It reduces the cost of entry, spreads risk, and offers an accessible way to participate in Nigeria's thriving property market while maintaining strong cultural and financial ties at home.
                                </p>
                            </div>

                            {/* The Next Trend in Property Investment */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">The Next Trend in Property Investment: Fractional Investing</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Lower Entry Barriers</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Instead of waiting for years to save enough to buy one property, diaspora Nigerians can start small by purchasing fractions of premium developments.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Shared Rental Income</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Rental income from tenants or short-term leases is shared among investors in line with their ownership percentage.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Access to Premium Projects</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Fractional investment gives investors access to high-value projects such as malls, office buildings, or gated estates that might otherwise be out of reach.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Diversification of Risk</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Pooling resources across multiple projects reduces risk while maximizing exposure to profitable opportunities.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Cultural and Emotional Value</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Owning even a fraction of real estate in Nigeria provides a sense of belonging and a strong cultural connection for diaspora investors.
                                </p>
                            </div>

                            {/* How Fractional Property Investment Works */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">How Fractional Property Investment Works</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Pick a Verified Property</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Choose from houses, apartments, commercial spaces, or land listed on trusted platforms.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Pool Resources</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Multiple investors fund a project together, sharing the cost.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Legal Ownership</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Investors receive certified digital documentation ensuring clear ownership rights.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Sharing of Earnings</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Rental income and appreciation are distributed proportionally to ownership shares.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Exit Flexibility</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Investors can divest or reinvest in other opportunities when needed.
                                </p>
                            </div>

                            {/* Types of Properties */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Types of Properties for Fractional Investment in Nigeria</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Residential Properties</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Apartments, gated communities, and short-let developments are trending and provide consistent rental income.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Commercial Properties</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Fractional ownership of office spaces, retail malls, and mixed-use developments is already generating strong long-term returns.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Land Banking</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Through fractional ownership of plots in high-performing locations, diaspora Nigerians can benefit from capital appreciation without the heavy financial burden of 100% ownership.
                                </p>
                            </div>

                            {/* Benefits */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Benefits of Fractional Real Estate Investment with Diasporawise</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Verified Projects</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Every property is legally vetted and compliance-checked.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Clarity</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Digital dashboards provide real-time investment updates.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Safety</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Secure, trackable internet payment protocols protect investors.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Flexibility</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Start small and expand your portfolio gradually.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Nation-Building</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Investments help bridge Nigeria's housing deficit and create jobs.
                                </p>
                            </div>

                            {/* How to Begin */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Begin Investing in Fractional Property in Nigeria</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Set Your Objectives</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Decide if your goal is rental income, appreciation, or diversification.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Choose a Credible Platform</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Use trusted sites like Diasporawise.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Select Property Type</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Pick residential, commercial, or land investments.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Check Documentation</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Verify all titles and approvals.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Use Secure Payments</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Only transact through genuine digital gateways.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Track Your Investment</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Monitor growth through online dashboards.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Think Long Term</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Approach fractional property as a wealth-building journey.
                                </p>
                            </div>

                            {/* Challenges */}
                            <div className="mb-12">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Challenges of Fractional Property Investment and Their Solutions</h2>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Fraud & Fake Titles</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Minimized by digital verification and vetted platforms.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Complicated Co-Ownership Agreements</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Simplified via transparent legal frameworks.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Liquidity Concerns</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Flexible exit options provided by investment platforms.
                                </p>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Currency Fluctuations</h3>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Investors should plan for exchange rate risks when investing from overseas.
                                </p>
                            </div>

                            {/* Conclusion */}
                            <div className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Conclusion: Making Home Ownership Available to Every Nigerian</h2>
                                <p className="text-gray-700 leading-relaxed mb-6">
                                    Fractional property investment in Nigeria is transforming the way Nigerians, especially those in the diaspora, buy and own real estate. It lowers financial barriers, reduces corruption risks, creates consistent income opportunities, and strengthens cultural ties while supporting nation-building.
                                </p>
                                <p className="text-gray-700 leading-relaxed">
                                    Platforms such as Diasporawise ensure that premium homes and commercial spaces are no longer reserved for the privileged few. Today, a piece of home is accessible to every Nigerian abroad—helping them grow wealth, create security, and own property in their homeland.
                                </p>
                            </div>
                        </article>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default FractionalPropertyInvestmentNigeria;
